import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { StatusBar } from '@capacitor/status-bar';

const SAFE_AREA_KEYS = {
  top: '--safe-area-top',
  right: '--safe-area-right',
  bottom: '--safe-area-bottom',
  left: '--safe-area-left',
} as const;

function syncSafeAreaWithCss(insets: Partial<Record<keyof typeof SAFE_AREA_KEYS, number>>) {
  if (typeof document === 'undefined') {
    return;
  }

  Object.entries(insets).forEach(([position, value]) => {
    const cssVar = SAFE_AREA_KEYS[position as keyof typeof SAFE_AREA_KEYS];
    document.documentElement.style.setProperty(cssVar, `${value ?? 0}px`);
  });
}

export function useCapacitorViewport() {
  useEffect(() => {
    if (Capacitor.getPlatform() === 'web') {
      syncSafeAreaWithCss({ top: 0, right: 0, bottom: 0, left: 0 });

      // ── Detección de teclado en PWA/web ─────────────────────────────────────
      // Con interactive-widget=resizes-content innerHeight se encoge con el teclado,
      // así que la detección por diferencia de alturas no sirve. Usamos focus/blur
      // sobre campos editables, que es fiable en todos los navegadores móviles.
      const isEditable = (el: EventTarget | null): boolean => {
        const node = el as HTMLElement | null;
        if (!node) return false;
        const tag = node.tagName?.toLowerCase();
        return tag === 'input' || tag === 'textarea' || tag === 'select' || node.isContentEditable;
      };

      const onFocusIn = (e: FocusEvent) => {
        if (isEditable(e.target)) {
          document.documentElement.classList.add('keyboard-open');
        }
      };

      const onFocusOut = () => {
        // Pequeño retraso: si el foco salta a otro input, no parpadea
        window.setTimeout(() => {
          if (!isEditable(document.activeElement)) {
            document.documentElement.classList.remove('keyboard-open');
          }
        }, 100);
      };

      document.addEventListener('focusin', onFocusIn);
      document.addEventListener('focusout', onFocusOut);

      return () => {
        document.removeEventListener('focusin', onFocusIn);
        document.removeEventListener('focusout', onFocusOut);
        document.documentElement.classList.remove('keyboard-open');
      };
    }

    let keyboardShowHandle: any;
    let keyboardHideHandle: any;

    StatusBar.setOverlaysWebView({ overlay: true }).catch(() => undefined);
    StatusBar.setBackgroundColor({ color: '#111827' }).catch(() => undefined);

    const setKeyboardHeight = (height: number) => {
      if (typeof document !== 'undefined') {
        document.documentElement.style.setProperty('--keyboard-height', `${height}px`);
      }
    };

    void Keyboard.addListener('keyboardWillShow', (event) => {
      setKeyboardHeight(event.keyboardHeight ?? 0);
    }).then((handle) => {
      keyboardShowHandle = handle;
    });

    void Keyboard.addListener('keyboardDidHide', () => {
      setKeyboardHeight(0);
    }).then((handle) => {
      keyboardHideHandle = handle;
    });

    return () => {
      if (keyboardShowHandle) {
        void keyboardShowHandle.remove();
      }

      if (keyboardHideHandle) {
        void keyboardHideHandle.remove();
      }
    };
  }, []);
}
